import { match } from '@gatsbyjs/reach-router';
import cx from 'classnames';
import { get, some } from 'lodash';
import React, { useRef } from 'react';

import { ReactComponent as Arrow } from '@skippay/core/assets/icons/icon-arrow-bottom.svg';
import TextPublic from '@skippay/core/components/TextPublic';
import useLocation from '@skippay/core/hooks/useLocation';

import { isActive, isOpen, link as linkStyle, linkArrow, linkTitle, submenu, submenuContent, submenuList, submenuLinkIcon, submenuSmall } from './index.module.sass';
import {SDescription, SLink, SSubmenuLink} from './index.styled';
import { useClickAway, useToggle } from 'react-use';

type LinkDestination = {
	to?:
		| string
		| {
				pathname: string;
		  };
	onClick?: () => void;
};

interface Props {
	className?: string;
	links: Array<
		{
			testId?: string;
			title: JSX.Element;
			icon?: JSX.Element;
			image?: JSX.Element;
			description?: JSX.Element;
			analyticsParam?: string;
		} & LinkDestination
	>;
	title?: JSX.Element;
	variant: 'small' | 'large';
}

function SubLink(link: Props['links'][number]): JSX.Element {
	const { description, icon, image, title, testId, analyticsParam, ...linkProps } = link;
	return (
		<SSubmenuLink>
			<SLink
				{...linkProps}
				variant='inherit'
				analyticsId={analyticsParam}
				textDecoration={['none', 'underline']}
				activeClassName="isActive"
				testId={testId || get(link, 'to.name')}
			>
				<span className="flex items-center">
					{icon && <span className={submenuLinkIcon}>{icon}</span>}
					{title}
				</span>
			</SLink>
			{image && title && description && (
				<SDescription>
					<div className="flex flex-col gap-2">
						{image}
						<TextPublic component="p" xlg stronger>
							{title}
						</TextPublic>
						<TextPublic component="p" colorLight>
							{description}
						</TextPublic>
					</div>
				</SDescription>
			)}
		</SSubmenuLink>
	);
}

const isAnyLinkActive = (links: Props['links'], location: Location) =>
	some(links, (link) => (link.to ? link.to && match(link.to, location.pathname) : false));

function DropdownLinks(props: Props): JSX.Element {
	const { location } = useLocation();
	const [isOpened, toggleIsOpened] = useToggle(false);
	const { links, variant, title } = props as Props;
	const isAnySubLinkActive = isAnyLinkActive(links, location);
	const ref = useRef(null);
	useClickAway(ref, () => toggleIsOpened(false));

	return (
		<li className={cx(linkStyle, isOpened && isOpen)} ref={ref}>
			<button
				type="button"
				className={cx(linkTitle, isAnySubLinkActive && isActive)}
				onClick={toggleIsOpened}
			>
				{title}
				<span className={linkArrow}>
					<Arrow />
				</span>
			</button>
			<div className={cx(submenu, variant === 'small' && submenuSmall)}>
				<div className={submenuContent}>
					<ul className={submenuList}>
						{links.map((link) => (
							<SubLink key={link.analyticsParam} {...link} />
						))}
					</ul>
				</div>
			</div>
		</li>
	);
};

export default DropdownLinks;
